import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { LEAD_STATUSES, labelFor, colorFor } from './leadStatus';

// Fonksiyon olarak tanimlanir ve her istekte cagrilir - modul-seviyesinde tek
// seferlik bir sabit olsaydi, token henuz localStorage'a yazilmadan (sayfa ilk
// yuklendiginde) donmus bir deger yakalanir ve giristen sonra hep gecersiz
// kalirdi (401 -> App.js'teki global interceptor oturumu kapatir).
const getAuthHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } });

const StatusBadge = ({ status }) => (
  <span style={{ background: colorFor(status), color: '#fff', fontSize: 12, padding: '2px 8px', borderRadius: 12 }}>
    {labelFor(status)}
  </span>
);

// Havuz + kilitleme + etiketleme is akisinin tamami burada. Bir kayda tiklamak
// kilit almaya calisir (POST /lock) - basarili olursa detay panel acilir, 409
// donerse kimin araligini gosterir. Etiketleme sonrasi liste otomatik yenilenir
// ki kilit durumu tum personel icin taze kalsin.
const QueueView = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ available: '1', exclude_customers: '1', status: '', q: '' });
  const [selected, setSelected] = useState(null);
  const [lockError, setLockError] = useState('');
  const [outcome, setOutcome] = useState('spoke');
  const [note, setNote] = useState('');
  const [nextCallAt, setNextCallAt] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.available === '1') params.available = '1';
      if (filters.exclude_customers === '1') params.exclude_customers = '1';
      if (filters.status) params.status = filters.status;
      if (filters.q) params.q = filters.q;
      const res = await axios.get('/api/admin/callcenter/leads', { ...getAuthHeaders(), params });
      setLeads(res.data.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchLeads();
    const interval = setInterval(fetchLeads, 20000);
    return () => clearInterval(interval);
  }, [fetchLeads]);

  const openLead = async (lead) => {
    setLockError('');
    try {
      await axios.post(`/api/admin/callcenter/leads/${lead.id}/lock`, {}, getAuthHeaders());
      const detail = await axios.get(`/api/admin/callcenter/leads/${lead.id}`, getAuthHeaders());
      setSelected(detail.data);
      setOutcome('spoke');
      setNote('');
      setNextCallAt('');
    } catch (err) {
      setLockError(err.response?.data?.error || 'Kayıt kilitlenemedi');
      fetchLeads();
    }
  };

  const releaseLead = async () => {
    if (!selected) return;
    try {
      await axios.post(`/api/admin/callcenter/leads/${selected.id}/release`, {}, getAuthHeaders());
    } catch (err) {
      console.error(err);
    } finally {
      setSelected(null);
      fetchLeads();
    }
  };

  const submitOutcome = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await axios.post(`/api/admin/callcenter/leads/${selected.id}/calls`, {
        outcome, note, next_call_at: outcome === 'call_again' && nextCallAt ? nextCallAt : null,
      }, getAuthHeaders());
      setSelected(null);
      fetchLeads();
    } catch (err) {
      alert('Hata: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
      <div style={{ flex: '1 1 320px' }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
          <input
            placeholder="Ara (isim/telefon)"
            value={filters.q}
            onChange={(e) => setFilters(f => ({ ...f, q: e.target.value }))}
            style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #d1d5db' }}
          />
          <select
            value={filters.status}
            onChange={(e) => setFilters(f => ({ ...f, status: e.target.value }))}
            style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #d1d5db' }}
          >
            <option value="">Tüm durumlar</option>
            {LEAD_STATUSES.map(s => <option key={s.slug} value={s.slug}>{s.label}</option>)}
          </select>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
            <input type="checkbox" checked={filters.available === '1'} onChange={(e) => setFilters(f => ({ ...f, available: e.target.checked ? '1' : '' }))} />
            Sadece boşta olanlar
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
            <input type="checkbox" checked={filters.exclude_customers === '1'} onChange={(e) => setFilters(f => ({ ...f, exclude_customers: e.target.checked ? '1' : '' }))} />
            Müşterileri gizle
          </label>
        </div>

        {lockError && <div style={{ color: '#dc2626', marginBottom: 8 }}>{lockError}</div>}

        <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, maxHeight: 520, overflowY: 'auto' }}>
          {loading && <div style={{ padding: 16, color: '#6b7280' }}>Yükleniyor...</div>}
          {!loading && leads.length === 0 && <div style={{ padding: 16, color: '#6b7280' }}>Havuzda kayıt yok.</div>}
          {leads.map(lead => (
            <div
              key={lead.id}
              onClick={() => openLead(lead)}
              style={{ padding: '10px 14px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', cursor: 'pointer' }}
            >
              <div>
                <div style={{ fontWeight: 600 }}>{lead.name}</div>
                <div style={{ fontSize: 12, color: '#6b7280' }}>{lead.city} · {lead.sector_raw}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div>{lead.phone || '—'}</div>
                <StatusBadge status={lead.status} />
                {lead.locked_username && <div style={{ fontSize: 11, color: '#d97706' }}>🔒 {lead.locked_username}</div>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {selected && (
        <div style={{ flex: '1 1 320px', maxWidth: 340, border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, height: 'fit-content' }}>
          <div style={{ fontWeight: 700, fontSize: 16 }}>{selected.name}</div>
          <div style={{ color: '#6b7280', fontSize: 13, marginBottom: 10 }}>{selected.address}</div>

          {selected.phone ? (
            <a href={`tel:${selected.phone}`} style={{ display: 'inline-block', background: '#16a34a', color: '#fff', padding: '8px 16px', borderRadius: 6, textDecoration: 'none', fontWeight: 600, marginBottom: 14 }}>
              📞 {selected.phone}
            </a>
          ) : (
            <div style={{ color: '#dc2626', marginBottom: 14 }}>Telefon numarası yok</div>
          )}

          <div style={{ marginBottom: 10 }}>
            <label style={{ fontSize: 13, fontWeight: 600 }}>Sonuç</label>
            <select value={outcome} onChange={(e) => setOutcome(e.target.value)} style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #d1d5db', marginTop: 4 }}>
              {LEAD_STATUSES.filter(s => s.slug !== 'not_called').map(s => <option key={s.slug} value={s.slug}>{s.label}</option>)}
            </select>
          </div>

          {outcome === 'call_again' && (
            <div style={{ marginBottom: 10 }}>
              <label style={{ fontSize: 13, fontWeight: 600 }}>Tekrar arama zamanı</label>
              <input type="datetime-local" value={nextCallAt} onChange={(e) => setNextCallAt(e.target.value)} style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #d1d5db', marginTop: 4 }} />
            </div>
          )}

          <div style={{ marginBottom: 10 }}>
            <label style={{ fontSize: 13, fontWeight: 600 }}>Not</label>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #d1d5db', marginTop: 4 }} />
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={submitOutcome} disabled={saving} style={{ flex: 1, padding: '8px 0', borderRadius: 6, border: 'none', background: '#2563eb', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>
              {saving ? 'Kaydediliyor...' : 'Kaydet'}
            </button>
            <button onClick={releaseLead} style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#1e293b', cursor: 'pointer' }}>
              Havuza bırak
            </button>
          </div>

          {selected.calls && selected.calls.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Geçmiş</div>
              {selected.calls.map(c => (
                <div key={c.id} style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>
                  <StatusBadge status={c.outcome} /> — {c.admin_username || 'bilinmiyor'} · {new Date(c.created_at).toLocaleString('tr-TR')}
                  {c.note && <div>{c.note}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default QueueView;
